/**
 * Foto-Schätzung im Hintergrund (28.10.2026) — der Platzhalter auf dem Gerät.
 *
 * Ablauf: Foto → Mahlzeit → „Schätzen lassen“ (Bestätigung je Foto). Sofort
 * steht im Tag ein **Platzhalter** („Foto wird geschätzt …“), die App lässt
 * sich verlassen. Der Server schätzt in seiner Schlange, die Mitteilung sagt
 * „fertig“; geholt wird außerdem bei jedem Abgleich, solange etwas offen ist.
 * Das Ergebnis öffnet dieses Gerät mit dem privaten Schlüssel des Kontos
 * (`openEstimateResult`). Erst „Passt“ oder „Anpassen“ macht daraus eine
 * normale Protokollzeile mit `estimate = 'photo'` und **derselben Kennung**
 * wie der Auftrag — so räumen auch andere Geräte ihren Platzhalter weg.
 *
 * **Der Platzhalter ist kein `food_log`-Eintrag.** Er zählt in keiner Summe:
 * nicht mit geratenen Werten und nicht als 0 (`totalsWithPending`). Die
 * Mahlzeiten-Erinnerung sieht ihn trotzdem — über den Auftrag beim Server
 * (Datum, Platz, Zustand), nicht über das Protokoll.
 */

import {
  newId,
  openEstimateResult,
  PHOTO_CONTENT_TYPE,
  PhotoEstimateSchema,
} from '@kraftwerk/shared';
import type { DaySlot, EstimateJobStatus, PhotoEstimate } from '@kraftwerk/shared';
import { ApiError, apiFetch } from '../api.js';
import { readIdentity } from '../crypto/e2e.js';
import { ensureIdentity } from '../crypto/householdKeys.js';
import { db } from './local.js';
import type { KraftwerkDatabase, LocalEstimateJob } from './local.js';
import { logFreeEntry } from './nutrition.js';

/** Was der Server zu einem Auftrag sagt (`EstimateJobView` in der API). */
export interface ServerEstimateJob {
  id: string;
  date: string;
  slot: DaySlot;
  status: EstimateJobStatus;
  position: number | null;
  reason: string | null;
  result: unknown;
  sealedResult: string | null;
  createdAt: string;
  closed: boolean;
}

const BASE = '/nutrition/photo-estimate/jobs';

/** Offene Platzhalter — für sie lohnt sich das Nachsehen bei jedem Abgleich. */
export function isPending(job: LocalEstimateJob): boolean {
  return (
    job.status === 'unsent' ||
    job.status === 'queued' ||
    job.status === 'running' ||
    (job.status === 'done' && job.result === null)
  );
}

/**
 * Was beim Abmelden verloren ginge: Aufträge, deren Foto nur hier liegt —
 * noch nicht gesendet, oder gescheitert mit Foto zum erneuten Senden. Alles
 * andere kennt der Server und kommt nach dem Anmelden zurück.
 */
export async function unsentEstimateCount(database: KraftwerkDatabase = db): Promise<number> {
  return (await database.estimate_jobs.toArray()).filter(
    (job) => job.status === 'unsent' || (job.status === 'failed' && job.photo !== null),
  ).length;
}

/** Ergebnis prüfen — auch das eigene Server-Ergebnis, nie eine stille 0. */
function checked(value: unknown): PhotoEstimate | null {
  const parsed = PhotoEstimateSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** Ergebnis lesbar machen: Klartext prüfen oder mit dem eigenen Schlüssel öffnen. */
async function openResult(
  job: ServerEstimateJob,
  database: KraftwerkDatabase,
): Promise<{ result: PhotoEstimate | null; sealedResult: string | null; broken: boolean }> {
  if (job.result) {
    const result = checked(job.result);
    return { result, sealedResult: null, broken: result === null };
  }
  if (!job.sealedResult) return { result: null, sealedResult: null, broken: true };
  const privateKey = await readIdentity(database);
  // Gerät noch gesperrt: das Chiffrat wartet, nichts wird verworfen.
  if (!privateKey) return { result: null, sealedResult: job.sealedResult, broken: false };
  try {
    const result = checked(await openEstimateResult(job.sealedResult, privateKey, job.id));
    return { result, sealedResult: null, broken: result === null };
  } catch {
    // Mit einem anderen Schlüssel verpackt (kann nicht sein) — sichtbar, nie still.
    return { result: null, sealedResult: job.sealedResult, broken: true };
  }
}

/** Den Stand des Servers auf den Platzhalter legen (Foto bleibt, wo es ist). */
async function apply(
  job: ServerEstimateJob,
  local: LocalEstimateJob | undefined,
  database: KraftwerkDatabase,
): Promise<void> {
  // Übernommen oder verworfen (auf diesem oder einem anderen Gerät).
  if (job.closed) {
    await database.estimate_jobs.delete(job.id);
    return;
  }
  const base: LocalEstimateJob = local ?? {
    id: job.id,
    date: job.date,
    slot: job.slot,
    status: job.status,
    position: null,
    reason: null,
    result: null,
    sealedResult: null,
    language: 'de',
    createdAt: job.createdAt,
    photo: null,
  };
  const next: LocalEstimateJob = {
    ...base,
    status: job.status,
    position: job.position,
    reason: job.reason,
  };
  if (job.status === 'done' && base.result === null) {
    const opened = await openResult(job, database);
    next.result = opened.result;
    next.sealedResult = opened.sealedResult;
    if (opened.broken) {
      next.status = 'failed';
      next.reason = 'invalid';
    }
  }
  // Ergebnis da: das Foto wird nicht mehr gebraucht.
  if (next.result) next.photo = null;
  await database.estimate_jobs.put(next);
}

/**
 * Den Auftrag an den Server senden. Offline bleibt er `unsent` und wird beim
 * nächsten Abgleich erneut versucht; jeder andere Fehler steht mit Grund da.
 */
export async function sendEstimate(
  id: string,
  database: KraftwerkDatabase = db,
  retried = false,
): Promise<LocalEstimateJob | undefined> {
  const job = await database.estimate_jobs.get(id);
  if (!job?.photo) return job;
  const query = new URLSearchParams({ lang: job.language, date: job.date, slot: job.slot });
  try {
    const body = await apiFetch<{ job: ServerEstimateJob }>(`${BASE}/${id}?${query}`, {
      method: 'POST',
      headers: { 'Content-Type': PHOTO_CONTENT_TYPE },
      body: job.photo,
    });
    await apply(body.job, job, database);
  } catch (error) {
    if (error instanceof ApiError && error.reason === 'identity' && !retried) {
      // Verschlüsseltes Konto ohne Schlüsselpaar: anlegen, dann noch einmal.
      if (await ensureIdentity(database).catch(() => false)) {
        return sendEstimate(id, database, true);
      }
    }
    const reason = !(error instanceof ApiError)
      ? 'offline'
      : error.status === 413
        ? 'tooLarge'
        : (error.reason ?? 'unknown');
    await database.estimate_jobs.update(id, {
      status: reason === 'offline' ? 'unsent' : 'failed',
      reason,
      position: null,
    });
  }
  return database.estimate_jobs.get(id);
}

/** Platzhalter anlegen und senden — die App muss danach auf nichts warten. */
export async function startEstimate(
  input: { date: string; slot: DaySlot; photo: Blob; language: 'de' | 'en' },
  database: KraftwerkDatabase = db,
): Promise<string> {
  const id = newId();
  await database.estimate_jobs.put({
    id,
    date: input.date,
    slot: input.slot,
    status: 'unsent',
    position: null,
    reason: null,
    result: null,
    sealedResult: null,
    language: input.language,
    createdAt: new Date().toISOString(),
    photo: input.photo,
  });
  void sendEstimate(id, database);
  return id;
}

/** „Erneut senden“ (abgebrochen, gescheitert) — derselbe Auftrag, dasselbe Foto. */
export async function resendEstimate(
  id: string,
  database: KraftwerkDatabase = db,
): Promise<LocalEstimateJob | undefined> {
  await database.estimate_jobs.update(id, { status: 'unsent', reason: null, position: null });
  return sendEstimate(id, database);
}

/** Beim Server abschließen — offline schadet nicht: er räumt nach 24 h selbst weg. */
async function closeOnServer(id: string): Promise<void> {
  await apiFetch(`${BASE}/${id}`, { method: 'DELETE' }).catch(() => undefined);
}

/**
 * „Passt“ / „Anpassen“: eine normale Protokollzeile mit eingefrorenen Werten,
 * dauerhaft „geschätzt“, Kennung = Auftrag. Erst danach ist der Platzhalter weg.
 */
export async function acceptEstimate(
  id: string,
  values: { label: string; kcal: number; protein: number; carbs: number; fat: number },
  note: string | null,
  database: KraftwerkDatabase = db,
): Promise<boolean> {
  const job = await database.estimate_jobs.get(id);
  if (!job || values.label.trim() === '') return false;
  await logFreeEntry(
    {
      date: job.date,
      slot: job.slot,
      label: values.label,
      macros: { kcal: values.kcal, protein: values.protein, carbs: values.carbs, fat: values.fat },
      estimate: 'photo',
      note,
      fixed: { id: job.id, eatenAt: job.createdAt },
    },
    database,
  );
  await database.estimate_jobs.delete(id);
  await closeOnServer(id);
  return true;
}

/** „Verwerfen“: Platzhalter, Foto und Auftrag weg — nichts wird eingetragen. */
export async function discardEstimate(id: string, database: KraftwerkDatabase = db): Promise<void> {
  await database.estimate_jobs.delete(id);
  await closeOnServer(id);
}

let lastFullRefresh = 0;
/** Ohne offene Platzhalter wird seltener nachgesehen (Aufträge anderer Geräte). */
export const IDLE_REFRESH_MS = 5 * 60 * 1000;

/**
 * Beim Öffnen und nach jedem Abgleich: Stand der Aufträge holen, Ergebnisse
 * öffnen, Ungesendetes erneut senden. Was der Server nicht (mehr) kennt:
 * übernommen (Protokollzeile mit derselben Kennung) → weg; noch offen →
 * „abgebrochen“ (Foto bleibt zum erneuten Senden); ein geöffneter Vorschlag
 * bleibt, bis man ihn übernimmt oder verwirft.
 */
export async function refreshEstimateJobs(
  database: KraftwerkDatabase = db,
  now: number = Date.now(),
): Promise<void> {
  const local = await database.estimate_jobs.toArray();
  const open = local.some(isPending);
  if (!open && now - lastFullRefresh < IDLE_REFRESH_MS) return;
  lastFullRefresh = now;

  for (const job of local.filter((entry) => entry.status === 'unsent')) {
    await sendEstimate(job.id, database);
  }

  const { jobs } = await apiFetch<{ jobs: ServerEstimateJob[] }>(BASE);
  const byId = new Map((await database.estimate_jobs.toArray()).map((job) => [job.id, job]));
  for (const job of jobs) await apply(job, byId.get(job.id), database);

  const known = new Set(jobs.map((job) => job.id));
  for (const job of byId.values()) {
    if (known.has(job.id) || job.status === 'unsent') continue;
    const logged = await database.food_log.get(job.id);
    if (logged) {
      await database.estimate_jobs.delete(job.id);
    } else if (job.status === 'queued' || job.status === 'running') {
      await database.estimate_jobs.update(job.id, {
        status: 'failed',
        reason: 'aborted',
        position: null,
      });
    } else if (job.status === 'failed' && !job.photo) {
      // Gescheitert, ohne Foto zum erneuten Senden, beim Server abgelaufen.
      await database.estimate_jobs.delete(job.id);
    } else if (job.status === 'done' && !job.result && !job.sealedResult) {
      await database.estimate_jobs.update(job.id, { status: 'failed', reason: 'aborted' });
    }
  }

  // Auf einem anderen Gerät übernommen: Protokollzeile mit derselben Kennung.
  for (const job of await database.estimate_jobs.toArray()) {
    if (await database.food_log.get(job.id)) await database.estimate_jobs.delete(job.id);
  }
}

/**
 * Wohin das Antippen der Mitteilung führt: der Tag des ältesten Platzhalters
 * mit Vorschlag (oder mit Fehler), sonst irgendeines offenen — `null` heißt heute.
 */
export async function estimateTargetDate(database: KraftwerkDatabase = db): Promise<string | null> {
  const jobs = (await database.estimate_jobs.toArray()).sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt),
  );
  const settled = jobs.find((job) => job.status === 'done' || job.status === 'failed');
  return (settled ?? jobs[0])?.date ?? null;
}

/** Nur für Tests: den Takt zurücksetzen. */
export function resetEstimateRefresh(): void {
  lastFullRefresh = 0;
}
